// Cookie consent for the web app, kept in step with pajakind.com
// (web-landing site/src/services/cookieConsent.js): same key, same record
// shape, same Consent Mode mapping. The record is written to a cookie on
// .pajakind.com so a choice made on either site carries over to the other
// (their localStorage is per origin), and to localStorage as a fallback for
// hosts outside that domain, such as Vercel previews and localhost.
//
// public/index.html reads the same record before Firebase loads gtag, to set
// the Consent Mode default. Keep the two in step.

export const CONSENT_KEY = "pajakind_cookie_consent";
export const CONSENT_EVENT = "cookieConsentUpdated";
const EXPIRY_DAYS = 365;

const GRANTED = "granted";
const DENIED = "denied";

const sharedDomain = () => {
  const host = window.location.hostname;
  return host === "pajakind.com" || host.endsWith(".pajakind.com")
    ? "pajakind.com"
    : null;
};

const readCookie = (name) => {
  const pair = document.cookie
    .split(";")
    .map((part) => part.trim())
    .find((part) => part.startsWith(`${name}=`));
  return pair ? decodeURIComponent(pair.slice(name.length + 1)) : null;
};

const parse = (raw) => {
  if (!raw) return null;
  try {
    const consent = JSON.parse(raw);
    // An expired record is no record: the visitor is asked again.
    if (consent.expiryDate && new Date(consent.expiryDate) < new Date()) {
      return null;
    }
    return consent;
  } catch (error) {
    return null;
  }
};

export const getConsent = () => {
  if (typeof window === "undefined") return null;
  const fromCookie = parse(readCookie(CONSENT_KEY));
  if (fromCookie) return fromCookie;
  try {
    return parse(window.localStorage.getItem(CONSENT_KEY));
  } catch (error) {
    return null;
  }
};

export const hasConsent = (category) => getConsent()?.[category] === true;

// GA writes _ga on the registrable domain, so expire on every scope it could
// have been set on.
const expireCookie = (name) => {
  const { hostname } = window.location;
  const parts = hostname.split(".");
  const scopes = [null, hostname, `.${hostname}`];
  if (parts.length > 2) {
    const registrable = parts.slice(-2).join(".");
    scopes.push(registrable, `.${registrable}`);
  }
  scopes.forEach((domain) => {
    document.cookie = `${name}=; expires=Thu, 01 Jan 1970 00:00:00 UTC; path=/;${
      domain ? ` domain=${domain};` : ""
    }`;
  });
};

const expireCookiesWithPrefix = (prefixes) => {
  document.cookie.split(";").forEach((pair) => {
    const name = pair.split("=")[0].trim();
    if (name && prefixes.some((prefix) => name.startsWith(prefix))) {
      expireCookie(name);
    }
  });
};

// Pushes the stored choice to gtag (GA4 through Firebase) and the Meta Pixel,
// and clears their cookies once the visitor has actually declined. Before the
// visitor answers, Consent Mode already keeps gtag off its cookies, and the
// cookies are shared with pajakind.com, so nothing is cleared then.
export const applyConsent = () => {
  const consent = getConsent();
  const analytics = consent?.analytics === true;
  const marketing = consent?.marketing === true;
  const functional = consent?.functional === true;

  if (typeof window.gtag === "function") {
    window.gtag("set", "ads_data_redaction", !marketing);
    window.gtag("consent", "update", {
      ad_storage: marketing ? GRANTED : DENIED,
      ad_user_data: marketing ? GRANTED : DENIED,
      ad_personalization: marketing ? GRANTED : DENIED,
      analytics_storage: analytics ? GRANTED : DENIED,
      functionality_storage: functional ? GRANTED : DENIED,
      personalization_storage: functional ? GRANTED : DENIED,
      security_storage: GRANTED,
    });
  }

  if (typeof window.fbq === "function") {
    window.fbq("consent", marketing ? "grant" : "revoke");
  }

  if (consent && !analytics) {
    expireCookiesWithPrefix(["_ga", "_gid", "_gat"]);
  }
  if (consent && !marketing) {
    expireCookiesWithPrefix(["_fbp", "_fbc", "_gcl"]);
  }
};

export const saveConsent = ({ functional, analytics, marketing }) => {
  const expiryDate = new Date();
  expiryDate.setDate(expiryDate.getDate() + EXPIRY_DAYS);

  const consent = {
    necessary: true,
    functional: functional === true,
    analytics: analytics === true,
    marketing: marketing === true,
    timestamp: new Date().toISOString(),
    expiryDate: expiryDate.toISOString(),
  };
  const value = JSON.stringify(consent);

  const domain = sharedDomain();
  const secure = window.location.protocol === "https:" ? "; Secure" : "";
  document.cookie = `${CONSENT_KEY}=${encodeURIComponent(value)}; expires=${expiryDate.toUTCString()}; path=/; SameSite=Lax${secure}${
    domain ? `; domain=${domain}` : ""
  }`;
  try {
    window.localStorage.setItem(CONSENT_KEY, value);
  } catch (error) {
    // Storage blocked: the cookie still carries the choice.
  }

  applyConsent();
  window.dispatchEvent(new CustomEvent(CONSENT_EVENT, { detail: consent }));
  return consent;
};

export const acceptAllConsent = () =>
  saveConsent({ functional: true, analytics: true, marketing: true });

export const rejectAllConsent = () =>
  saveConsent({ functional: false, analytics: false, marketing: false });
